import React from 'react'
import PropTypes from 'prop-types'
import { View, StyleSheet } from '@react-pdf/renderer'
import {
  getRiskClassification,
  getRisks,
  getRisksForDisplay,
  colorByRisks,
} from '@util/classificationUtil'

import { hexToRgbA } from '@util/stringUtil'
import BarChart from '@generators/pdfComponents/icons/BarChart'
import colors from '@settings/styleSettings'
import { Box } from './Box'
import Text from './Text'

const HEALTH_MESSAGES = {
  has: 'O controle da hipertensão é essencial para a saúde cardiovascular.',
  dm: 'O monitoramento da glicemia é fundamental para o controle do diabetes.',
  cholesterol: 'Níveis de colesterol controlados reduzem riscos cardíacos.',
  overweight: 'A gestão do peso corporal auxilia na prevenção de diversas patologias.',
}

export const RiskClassification = ({
  has, dm, cholesterol, imc
}) => {
  const risks = getRisks(has, dm, cholesterol, imc)
  const risksLength = Array.isArray(risks) ? risks.length : risks
  const riskClassification = getRiskClassification(risksLength)
  const riskColor = colorByRisks(risksLength)

  const getDescriptionText = () => {
    if (!Array.isArray(risks) || risks.length === 0) {
      if (risksLength < 0) {
        return 'Não foi possível classificar seu risco por falta de dados coletados.\n\nProcure realizar a coleta para receber orientações mais precisas.'
      }
      return 'Seus índices estão dentro da normalidade.\n\nMantenha hábitos saudáveis como alimentação equilibrada e exercícios físicos para continuar assim.'
    }

    const messages = []
    if (has) messages.push(HEALTH_MESSAGES.has)
    if (dm) messages.push(HEALTH_MESSAGES.dm)
    if (cholesterol) messages.push(HEALTH_MESSAGES.cholesterol)
    if (imc > 25) messages.push(HEALTH_MESSAGES.overweight)

    return `Identificamos alterações nos seus índices${getRisksForDisplay(risks)}\n\n${messages.join(' ')}\n\nEsses fatores exigem cuidados para não evoluírem. Confira abaixo as orientações preventivas.`
  }

  const descriptionText = getDescriptionText()

  return (
    <View style={styles.container}>
      <Box height={160} width={188} align="center">
        <View style={styles.riskContainer}>
          <View
            style={[
              styles.iconContainer,
              { backgroundColor: hexToRgbA(riskColor, '0.16') },
            ]}
          >
            <BarChart color={riskColor} />
          </View>
          <View>
            <Text style={{ color: colors.strongGray }}>
              Classificação de risco
            </Text>
            <Text
              style={[
                styles.riskText,
                { color: riskColor, fontFamily: 'Inter-Bold' },
              ]}
            >
              {riskClassification}
            </Text>
          </View>
        </View>
      </Box>
      <Box height={160} width={320} align="center" padding={12}>
        <Text style={{ color: colors.strongGray, textAlign: 'left' }}>
          Seu risco atual é
          {' '}
          <Text style={{ fontFamily: 'Inter-Bold' }}>{riskClassification}</Text>
          {'\n\n'}
          {descriptionText}
        </Text>
      </Box>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '14 0',
  },
  riskContainer: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
  },
  iconContainer: {
    alignSelf: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 6,
    borderRadius: '50%',
    width: 40,
    height: 40,
  },
  riskText: {
    fontSize: 14,
  },
})

RiskClassification.propTypes = {
  has: PropTypes.bool.isRequired,
  dm: PropTypes.bool.isRequired,
  cholesterol: PropTypes.bool.isRequired,
  imc: PropTypes.number.isRequired,
}
